import { useEffect, memo, CSSProperties, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectValidParams, selectGridData } from '@pages/reporting-pages/deliveries-main/store/selectors';
import { createGridData } from '@pages/reporting-pages/deliveries-main/store/slice';
import { useLazyGetDeliveriesMainQuery } from '@api/order-reporting/deliveryApi';
import { GridLoading, GridError, GridEmpty, GridNoParams } from 'components/global/GridElements';
import { useGridSorting } from 'components/filtering/filter-logic/useGridSorting';
import { useGridFiltering } from 'components/filtering/filter-logic/useGridFiltering';
import { useGridGrouping } from 'components/filtering/filter-logic/useGridGrouping';
import GridHeader from '@pages/reporting-pages/deliveries-main/grid/GridHeader';
import GridSummary from '@pages/reporting-pages/deliveries-main/grid/GridSummary';
import GridRow from '@pages/reporting-pages/deliveries-main/grid/GridRow';
import { MainReport } from '@api/order-reporting/types-deliveries/DeliveriesReports';
import TableHeader from '@pages/reporting-pages/deliveries-main/grid/TableHeader';
import { getObjectEntries } from '@utils/typescriptGuards';
import { headersObject } from '@pages/reporting-pages/deliveries-main/headersObject';



const Grid = () => {

	const validParams = useSelector(selectValidParams);
	const gridData = useSelector(selectGridData);
	const [ runQuery, { data, isLoading, isFetching, isError, isSuccess } ] = useLazyGetDeliveriesMainQuery();
	const [ filteredData, filterObject, handleSearchValues, handleAddFilterValues, clearFilter ] = useGridFiltering(gridData);
	const [ sortedData, sortObject, handleSorting, clearSorting ] = useGridSorting(filteredData);
	const [ groupedData ] = useGridGrouping(sortedData, 'country');

	const dispatch = useDispatch();
	const runCreateGridData = (data: MainReport[] | undefined | null) => dispatch(createGridData(data));

	const className = 'app-grid deliveries-main-grid';
	const style: CSSProperties = {
		gridTemplateColumns: headersObject.map(x => x.width).join(' '),
	}

	
	useEffect(() => {
		if (validParams) {
			runQuery({ ...validParams }, true);
		}
	}, [ validParams ]);


	useEffect(() => {
		if (data) {
			runCreateGridData(data);
		}
	}, [ data ]);

	
	if (!validParams) return (
		<div className={className} style={style}>
			<TableHeader />
			<GridHeader />
			<GridNoParams headersLength={headersObject.length} />
		</div>
	);

	if (isLoading || isFetching) return (
		<div className={className} style={style}>
			<TableHeader />
			<GridHeader />
			<GridLoading headersLength={headersObject.length} />
		</div>
	);

	if (isError || !data) return (
		<div className={className} style={style}>
			<TableHeader />
			<GridHeader />
			<GridError headersLength={headersObject.length} />
		</div>
	);
	
	if (isSuccess && gridData && gridData.length === 0) return (
		<div className={className} style={style}>
			<TableHeader />
			<GridHeader />
			<GridEmpty headersLength={headersObject.length} />
		</div>
	);

	return (
		<div className={className} style={style}>

			<TableHeader />
			
			<GridHeader
				filterObject={filterObject}
				sortObject={sortObject}
				onSearchValues={handleSearchValues}
				onAddFilterValues={handleAddFilterValues}
				onClearFilter={clearFilter}
				onSorting={handleSorting}
				onClearSorting={clearSorting}
			/>

			<GridSummary data={sortedData} />
			

			{groupedData && getObjectEntries(groupedData).map(objectEntry =>
				<Fragment key={`${objectEntry[0]}`}>
					{objectEntry[1] && objectEntry[1].map((x, i) =>
						<GridRow
							key={`id-deliveries-main-grid-${x.country}-${x.executionPlaceName}`}
							item={x}
							rowIndex={i}
						/>
					)}

					{objectEntry[1] &&
						<GridSummary
							data={objectEntry[1]}
							isSubtotal={true}
							label={`${objectEntry[0]} Celkem`}
						/>
					}
					
				</Fragment>
			)}
		</div>
	)
}

export default memo(Grid);